
import { useState } from "react";
import {
    X,
    Edit3,
    CheckCircle,
    MessageSquareMore,
    User,
    Tag,
    ShieldCheck,
    Shield,
    BadgeCheck,
    Save,
    XCircle,
} from "lucide-react";
import { Database } from 'lucide-react';

export default function ModalPop({ term, onClose, onSave }) {
    const termData = {
        term: term?.term || "Bias",
        status: term?.status || "Approved",
        department: term?.department || "sd-demand-planning",
        definition:
            term?.fullDefinition ||
            term?.definition ||
            "A systematic tendency in forecasts to consistently over-predict or under-predict actual demand over time.",
        formula: term?.formula || "Bias = Σ(Forecast − Actual) / n",
        owner: term?.owner || "Sarah Chen",
        classification: term?.classification || "Internal",
        certificate: term?.certificate || "Verified",
        date: term?.lastUpdated || term?.date || "2024-01-30",
        qualityScore: term?.qualityScore || 89,
    };

    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState(termData);

    if (!term) return null;

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditedData(termData);
        setIsEditing(false);
    };

    const handleSave = () => {
        if (onSave) {
            onSave(editedData);
        }
        setIsEditing(false);
    };

    const handleChange = (field, value) => {
        setEditedData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl">
                <div className="flex min-h-[520px]">
                    {/* LEFT PANEL */}
                    <div className="flex-1 border-r p-3">
                        {/* Header */}
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-[3px]">
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editedData.term}
                                            onChange={(e) => handleChange('term', e.target.value)}
                                            className="text-lg font-semibold border-b-2 border-blue-500 outline-none px-1"
                                        />
                                    ) : (
                                        <h2 className="text-lg font-semibold">{editedData.term}</h2>
                                    )}
                                    <CheckCircle className="h-4 text-green-400" />
                                </div>
                                <p className="text-xs text-black mt-[10px]">
                                    <span className="border border-gray-300 px-[4px] py-[4px] rounded-full mr-[4px]">TERM</span> • {editedData.department}
                                </p>
                            </div>

                            {isEditing ? (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSave}
                                        className="flex items-center gap-1 rounded bg-blue-600 text-white px-3 py-1 text-sm hover:bg-blue-700"
                                    >
                                        <Save className="h-4 w-4" /> Save
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className="flex items-center gap-1 rounded border px-3 py-1 text-sm hover:bg-gray-100"
                                    >
                                        <XCircle className="h-4 w-4" /> Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleEdit}
                                    className="flex items-center gap-1 rounded border px-2 py-1 text-sm hover:bg-gray-100"
                                >
                                    <Edit3 className="h-4 w-4" /> Edit
                                </button>
                            )}
                        </div>

                        {/* Tabs */}
                        <div className="mt-4 flex gap-6 border-b text-sm">
                            <button className="border-b-2 border-blue-600 pb-2 font-medium">
                                Overview
                            </button>
                            <button className="pb-2 text-black">Feedback (0)</button>
                        </div>

                        {/* Definition */}
                        <div className="mt-4">
                            <p className="text-sm font-medium">Definition</p>
                            {isEditing ? (
                                <textarea
                                    value={editedData.definition}
                                    onChange={(e) => handleChange('definition', e.target.value)}
                                    className="mt-1 w-full text-sm text-black border border-gray-300 rounded p-2 outline-none focus:border-blue-500"
                                    rows={3}
                                />
                            ) : (
                                <p className="mt-1 text-sm text-black">
                                    {editedData.definition}
                                </p>
                            )}
                        </div>

                        {/* Formula */}
                        <div className="mt-4">
                            <div className="mt-2 rounded bg-[#A3A2A9] p-3">
                                <p className="text-sm font-medium text-black mb-[6px]">Formula</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editedData.formula}
                                        onChange={(e) => handleChange('formula', e.target.value)}
                                        className="w-full font-mono text-sm text-[#7C3AED] bg-white border border-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                                    />
                                ) : (
                                    <div className="font-mono text-sm text-[#7C3AED]">
                                        {editedData.formula}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Business Context */}
                        <div className="mt-4">
                            <p className="text-sm font-medium">Business Context</p>
                            {isEditing ? (
                                <textarea
                                    value="Indicates forecast quality issues that need process or model adjustments."
                                    className="mt-1 w-full text-sm text-blck border border-gray-300 rounded p-2 outline-none focus:border-blue-500"
                                    rows={2}
                                />
                            ) : (
                                <p className="mt-1 text-sm text-black">
                                    Indicates forecast quality issues that need process or model adjustments.
                                </p>
                            )}
                        </div>

                        {/* Data Type & Source */}
                        <div className="mt-4 flex gap-10 text-sm text-black">
                            <div className="flex gap-[4px] items-center justify-center">
                                <Database size={14} /> Data Type: Number
                            </div>
                            <div className="flex gap-[4px] items-center justify-center">
                                <Database size={14} /> Source: SAP IBP
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="mt-4">
                            <p className="text-sm font-medium flex gap-[4px] items-center">
                                <Tag size={12} />
                                Tags
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <span className="rounded bg-gray-600 text-white px-2 py-1 text-xs rounded-lg">
                                    Forecast
                                </span>
                                <span className="rounded bg-gray-600 text-white px-2 py-1 text-xs rounded-lg">
                                    Quality
                                </span>
                                <span className="rounded bg-gray-600 text-white px-2 py-1 text-xs rounded-lg">
                                    Statistics
                                </span>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="mt-6 flex gap-3 justify-end">
                            <button className="flex items-center gap-1 text-black rounded border px-3 py-1 text-sm hover:bg-gray-100">
                                <MessageSquareMore size={10} /> Comment
                            </button>
                            <button className="flex items-center gap-1 text-black rounded border px-3 py-1 text-sm hover:bg-gray-100">
                                <MessageSquareMore size={10} /> Report Issue
                            </button>
                        </div>
                    </div>

                    {/* RIGHT PANEL */}
                    <div className="relative w-80 bg-[#e3dede] p-3">
                        <X
                            onClick={onClose}
                            className="absolute right-1 top-4 h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
                        />

                        <p className="text-xs font-semibold mb-[5px] text-black">OWNERS</p>
                        <div className="mb-3 flex items-center text-sm gap-2">
                            <User className="h-4 text-black" />
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editedData.owner}
                                    onChange={(e) => handleChange('owner', e.target.value)}
                                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500"
                                />
                            ) : (
                                editedData.owner
                            )}
                        </div>

                        <p className="mb-2 text-xs font-semibold text-black">
                            CLASSIFICATION
                        </p>
                        <p className="mb-4 text-sm flex gap-[10px] items-center">
                            <Shield size={10} />
                            {isEditing ? (
                                <select
                                    value={editedData.classification}
                                    onChange={(e) => handleChange('classification', e.target.value)}
                                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500"
                                >
                                    <option value="Internal">Internal</option>
                                    <option value="Public">Public</option>
                                    <option value="Confidential">Confidential</option>
                                </select>
                            ) : (
                                editedData.classification
                            )}
                        </p>

                        <p className="mb-2 text-xs font-semibold text-black">
                            CERTIFICATE
                        </p>
                        <div className="mb-1 flex items-center text-sm text-[#15803D] ">
                            <BadgeCheck className="h-4 text-[#15803D] " />
                            {editedData.certificate}
                        </div>
                        <p className="text-black text-xs">{editedData.date}</p>

                        <p className="mb-2 text-xs font-semibold text-black mt-4">STATUS</p>
                        {isEditing ? (
                            <select
                                value={editedData.status}
                                onChange={(e) => handleChange('status', e.target.value)}
                                className="mb-4 w-full border border-gray-300 rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
                            >
                                <option value="Approved">Approved</option>
                                <option value="Pending">Pending</option>
                                <option value="Draft">Draft</option>
                            </select>
                        ) : (
                            <span className="mb-4 inline-block rounded px-2 py-1 text-xs rounded-lg text-[#15803D] bg-[#22C55E29]">
                                {editedData.status}
                            </span>
                        )}

                        <p className="mt-4 mb-2 text-xs font-semibold text-black">
                            DEPARTMENT
                        </p>
                        {isEditing ? (
                            <input
                                type="text"
                                value={editedData.department}
                                onChange={(e) => handleChange('department', e.target.value)}
                                className="mb-4 w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500"
                            />
                        ) : (
                            <p className="mb-4 text-sm">{editedData.department}</p>
                        )}

                        <p className="mb-2 text-xs font-semibold text-black">
                            LAST UPDATED
                        </p>
                        <p className="mb-6 text-sm">{editedData.date}</p>

                        <p className="mb-2 text-xs font-semibold text-black">
                            QUALITY SCORE
                        </p>
                        {isEditing ? (
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={editedData.qualityScore}
                                onChange={(e) => handleChange('qualityScore', parseInt(e.target.value))}
                                className="w-full mb-1"
                            />
                        ) : (
                            <div className="h-2 w-full rounded">
                                <div
                                    className="h-2 rounded bg-[#15803D] "
                                    style={{ width: `${editedData.qualityScore}%` }}
                                />
                            </div>
                        )}
                        <p className="mt-1 text-xs">{editedData.qualityScore}%</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
